export const UnitType = {
  MobileStation: 'MobileStation',
  Repeater: 'Repeater',
} as const;
export type UnitType = typeof UnitType[keyof typeof UnitType];

export const UnitStatus = {
  Adopted: 'adopted',
  Unadopted: 'unadopted',
  /** Site-facing view of a released binding — immutable tombstone (D9). */
  Released: 'released',
} as const;
export type UnitStatus = typeof UnitStatus[keyof typeof UnitStatus];

export const UserRole = {
  SuperAdmin: 'SuperAdmin',
  OrgAdmin: 'OrgAdmin',
  LocalAdmin: 'LocalAdmin',
  SiteAdmin: 'SiteAdmin',
  Viewer: 'Viewer',
} as const;
export type UserRole = typeof UserRole[keyof typeof UserRole];

// ── Device lifecycle (rev-2 overhaul, §0/§C1/§C2) ──────────────────────────
// Mirrors backend/src/types/index.ts — kept in sync by hand (frontend has no
// shared package import for backend types).

/** Device role within a site (rev-2 board). */
export type DeviceRole = 'leaf' | 'cellular' | 'gateway';

/**
 * Lifecycle state of a physical device (distinct from `UnitStatus`, which is
 * the site-facing view of a binding). `manufactured → adopted → released →
 * adopted …`; `any → retired`. `physical_reprovision_required` is the
 * terminal escape set by super-admin `force-release`.
 */
export type LifecycleStatus =
  | 'manufactured'
  | 'adopted'
  | 'released'
  | 'retired'
  | 'physical_reprovision_required';

/** Pending secret-rotation record. `null`/absent when no rotation is in flight. */
export interface PendingRotation {
  generation: number;
  state: 'issued' | 'confirmed';
}

/** The `DEVICE#{uuid}/METADATA` anchor as returned by `GET /api/v1/devices` (super-admin). */
export interface Device {
  deviceUuid: string;
  serialNumber: string;
  nodeId: number;
  role: DeviceRole;
  lifecycleStatus: LifecycleStatus;
  currentBindingId: string | null;
  configVersion: number;
  pendingRotation: PendingRotation | null;
  createdAt: string;
  adoptedAt?: string;
  releasedAt?: string;
  lastConfigPullAt?: string;
}

/** `GET /api/v1/devices/{serial}/public-status` (C5) — no secrets, no auth. */
export interface PublicDeviceStatus {
  lifecycleStatus: LifecycleStatus;
  adoptable: boolean;
}
